import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import QRScanner from "utils/QRScanner";
import { showConfirm, showError } from "utils/swalHelper";
import * as Yup from "yup";
import QuotationItemTable from "./QuotationItemTable";
import PageContainer from "components/PageContainer/PageContainer";

interface FormValues {
  quotation_date: string;
  dispatch_from_warehouse: any;
  customer: any;
  items: any;
  remarks: string;
  enquiry_no?: string;
  enquiry_id?: string;
}

interface QuotationFormProps {
  enquiryId?: string;
}

const EnquiryQuotationForm: React.FC<QuotationFormProps> = ({ enquiryId }) => {
  document.title = "enquiry quotation form";
  const { id } = useParams();
  const navigate = useNavigate();
  const [formLoding, setFormLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<any>([]);
  const [dispatchWarehouseOptions, setDispatchWarehouseOptions] = useState<any>(
    []
  );
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [quotationData, setQuotationData] = useState<any>(null);
  const [bagQuantityValidation, setBagQuantityValidation] = useState<
    Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  >([]);
  const [showScanner, setShowScanner] = useState(false);
  const [isScanningItem, setIsScanningItem] = useState<boolean>(false);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      quotation_date: new Date().toISOString().split("T")[0],
      dispatch_from_warehouse: null,
      customer: null,
      items: [],
      remarks: "",
      enquiry_no: "",
      enquiry_id: enquiryId || "",
    },
    validationSchema: Yup.object({
      quotation_date: Yup.string().required("Quotation date is required"),
      dispatch_from_warehouse: Yup.object()
        .nullable()
        .required("Dispatch from warehouse is required"),
      customer: Yup.object().nullable().required("Customer name is required"),
      items: Yup.array()
        .min(1, "At least one item is required")
        .required("At least one item is required"),
    }),
    onSubmit: async (values) => {
      console.log("This is update");
      // Validate items before submission
      if (!values.items || values.items.length === 0) {
        formik.setFieldTouched("items", true);
        toast.error("At least one item is required");
        return;
      }

      // Validate that all items have bags array with length > 0
      const itemsWithoutBags = values.items.filter(
        (item: any) =>
          !item.bags || !Array.isArray(item.bags) || item.bags.length === 0
      );
      if (itemsWithoutBags.length > 0) {
        formik.setFieldTouched("items", true);
        toast.error(
          `All items must have at least one bag selected. ${itemsWithoutBags.length} item(s) are missing bags.`
        );
        return;
      }

      // Validate required fields for each item
      const itemsWithMissingFields = values.items.filter((item: any) => {
        const categoryId = item?.category?.category_id || item?.category_id;
        const subCategoryId =
          item?.sub_category?.sub_category_id || item?.sub_category_id;
        const itemId = item?.item?.item_id || item?.item_id;
        const hsnCodeId =
          item?.item?.hsn_code?.hsn_code_id || item?.hsn_code_id;
        const unitId =
          item?.item?.unit?.unit_id || item?.unit?.unit_id || item?.unit_id;
        const gstId = item?.gst?.gst_id || item?.gst_id;

        return (
          !categoryId ||
          !subCategoryId ||
          !itemId ||
          !hsnCodeId ||
          !unitId ||
          !gstId
        );
      });

      if (itemsWithMissingFields.length > 0) {
        formik.setFieldTouched("items", true);
        toast.error(
          `Some items are missing required fields (Category, Sub-Category, Item, HSN Code, Unit, or GST). Please check all items.`
        );
        return;
      }

      // Validate that required quantity doesn't exceed available stock for each item
      for (const item of values.items) {
        const requiredQty =
          parseFloat(String(item.quantity || item.required_quantity)) || 0;

        if (!item.bags || !Array.isArray(item.bags) || item.bags.length === 0) {
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }" must have at least one bag selected`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Calculate total issue quantity from all bags
        const totalIssueQuantity = item.bags.reduce((sum: number, bag: any) => {
          return sum + (parseFloat(String(bag.issue_quantity)) || 0);
        }, 0);

        // Validate that total issue quantity equals required quantity
        if (Math.abs(totalIssueQuantity - requiredQty) > 0.01) {
          // Allow small floating point differences
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }": Total Issue Quantity (${totalIssueQuantity.toFixed(
              2
            )}) must equal Required Quantity (${requiredQty.toFixed(2)})`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Calculate total available quantity from all bags
        const totalAvailableQuantity = item.bags.reduce(
          (sum: number, bag: any) => {
            return sum + (parseFloat(String(bag.available_quantity)) || 0);
          },
          0
        );

        // Validate that required quantity doesn't exceed total available stock
        if (requiredQty > totalAvailableQuantity) {
          toast.error(
            `Item "${
              item.item?.item_name || item.item_name || "Unknown"
            }": Required Quantity (${requiredQty.toFixed(
              2
            )}) exceeds Total Available Stock (${totalAvailableQuantity.toFixed(
              2
            )})`
          );
          formik.setFieldTouched("items", true);
          return;
        }

        // Validate that each bag's issue quantity doesn't exceed its available quantity
        for (const bag of item.bags) {
          const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
          const availableQty = parseFloat(String(bag.available_quantity)) || 0;

          if (issueQty > availableQty) {
            toast.error(
              `Item "${
                item.item?.item_name || item.item_name || "Unknown"
              }": Bag "${bag.bag_no}" - Issue Quantity (${issueQty.toFixed(
                2
              )}) exceeds Available Quantity (${availableQty.toFixed(2)})`
            );
            formik.setFieldTouched("items", true);
            return;
          }
        }
      }

      // Calculate financial summary values from items
      const subtotalBeforeDiscount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.amount || 0);
        }, 0) || 0;

      const totalItemDiscounts =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.discount_amount || 0);
        }, 0) || 0;

      const netTaxableValue = subtotalBeforeDiscount - totalItemDiscounts;

      const totalGst =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.gst_amount || 0);
        }, 0) || 0;

      const totalAmount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.total_amount || 0);
        }, 0) || 0;

      const payload = {
        quotation_date: values?.quotation_date,
        dispatch_from_warehouse_id:
          values?.dispatch_from_warehouse?.warehouse_id,
        customer_id: values?.customer?.customer_id,
        customer_details: {
          ...values?.customer,
        },
        subtotal_before_discount: parseFloat(subtotalBeforeDiscount.toFixed(2)),
        total_item_discounts: parseFloat(totalItemDiscounts.toFixed(2)),
        net_taxable_value: parseFloat(netTaxableValue.toFixed(2)),
        total_gst: parseFloat(totalGst.toFixed(2)),
        total_amount: parseFloat(totalAmount.toFixed(2)),
        remarks: values?.remarks,
        enquiry_no: values?.enquiry_no || null,
        enquiry_id: id || values?.enquiry_id || null,
        quotation_items: values?.items?.map((item: any) => ({
          ...item,
          category_id: item?.category?.category_id || item?.category_id,
          category_name: item?.category?.category_name || item?.category_name,
          sub_category_id:
            item?.sub_category?.sub_category_id || item?.sub_category_id,
          sub_category_name:
            item?.sub_category?.sub_category_name || item?.sub_category_name,
          item_id: item?.item?.item_id || item?.item_id,
          item_name: item?.item?.item_name || item?.item_name,
          item_code: item?.item_code,
          item_details: item?.item_details || {},
          // HSN Code fields - check both nested and direct properties
          hsn_code_id:
            item?.item?.hsn_code?.hsn_code_id || item?.hsn_code_id || null,
          hsn_code_name:
            item?.item?.hsn_code?.hsn_code || item?.hsn_code_name || null,
          // Unit fields - check both nested and direct properties, handle unit as string or object
          unit_id:
            item?.item?.unit?.unit_id ||
            item?.unit?.unit_id ||
            item?.unit_id ||
            null,
          unit_name:
            typeof item?.unit === "string"
              ? item?.unit
              : item?.item?.unit?.unit_name ||
                item?.unit?.unit_name ||
                item?.unit_name ||
                null,
          unit_symbol: item?.item?.unit?.unit_symbol || null,
          // Quantity fields
          required_quantity:
            parseFloat(item?.quantity || item?.required_quantity) || 0,
          quantity: parseFloat(item?.quantity || item?.required_quantity) || 0,
          // Bags
          bags:
            item?.bags && Array.isArray(item.bags)
              ? item.bags.map((bag: any) => ({
                  ...bag,
                  available_quantity:
                    typeof bag.available_quantity === "string"
                      ? parseFloat(bag.available_quantity)
                      : bag.available_quantity,
                  issue_quantity:
                    typeof bag.issue_quantity === "string"
                      ? parseFloat(bag.issue_quantity)
                      : bag.issue_quantity,
                }))
              : item?.bags,
          // Price fields
          selling_price: parseFloat(item?.rate || item?.selling_price) || 0,
          rate: parseFloat(item?.rate || item?.selling_price) || 0,
          amount: parseFloat(item?.amount) || 0,
          // Discount fields
          discount: item?.discount ? parseFloat(item?.discount) : 0,
          discount_type:
            item?.discount_type === "%"
              ? "percentage"
              : item?.discount_type === "₹"
              ? "amount"
              : item?.discount_type || "percentage",
          discount_amount: parseFloat(item?.discount_amount) || 0,
          // GST fields - check both nested and direct properties
          gst_id: item?.gst?.gst_id || item?.gst_id || null,
          gst_percent: item?.gst?.gst_percent
            ? parseFloat(item?.gst?.gst_percent)
            : item?.gst_percent
            ? parseFloat(item?.gst_percent)
            : 0,
          gst_amount: parseFloat(item?.gst_amount) || 0,
          // Total and remarks
          total_amount: parseFloat(item.total_amount) || 0,
          remarks: item?.remark || item?.remarks || null,
        })),
      };

      try {
        setFormLoading(true);
        // if (id) {
        //     await Axios.put(`/quotation/${id}/update/`, payload);
        //     toast.success("Quotation Updated Successfully");
        //     navigate("/quotation");
        // } else {
        // }
        await Axios.post(api?.quotation?.create, payload);
        toast.success("Quotation Created Successfully");
        navigate("/quotation");
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } finally {
        setFormLoading(false);
      }
    },
  });

  const tableValidationSchema = Yup.object().shape({
    category: Yup.object().nullable().required("Category is required"),
    sub_category: Yup.object().nullable().required("Sub Category is required"),
    item: Yup.object().nullable().required("Item Name is required"),
    item_code: Yup.string().trim().required("Item Code is required"),
    unit: Yup.string().trim().required("Unit is required"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .positive("Quantity must be greater than 0")
      .required("Quantity is required"),
    bags: Yup.array()
      .of(
        Yup.object().shape({
          inventory_item_id: Yup.mixed().required(),
          bag_no: Yup.string().required(),
          available_quantity: Yup.number().required(),
          issue_quantity: Yup.number().required(),
        })
      )
      .min(1, "At least one bag is required")
      .required("At least one bag is required"),
    rate: Yup.number()
      .typeError("Rate must be a number")
      .positive("Rate must be greater than 0")
      .required("Rate is required"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required"),
    discount: Yup.number()
      .typeError("Discount must be a number")
      .min(0, "Discount cannot be negative")
      .nullable(),
    discount_type: Yup.string()
      .oneOf(["%", "₹"], "Invalid discount type")
      .required("Discount type is required"),
    discount_amount: Yup.number()
      .typeError("Discount amount must be a number")
      .min(0, "Discount amount cannot be negative")
      .nullable(),
    gst: Yup.object().nullable().required("GST is required"),
    gst_amount: Yup.number()
      .typeError("GST amount must be a number")
      .min(0, "GST amount cannot be negative")
      .required("GST amount is required"),
    total_amount: Yup.number()
      .typeError("Total amount must be a number")
      .min(0, "Total amount cannot be negative")
      .required("Total amount is required"),
    remark: Yup.string().trim().nullable(),
  });

  const tableFormik = useFormik<any>({
    enableReinitialize: true,
    initialValues: {
      category: null,
      sub_category: null,
      item: null,
      item_code: "",
      unit: "",
      quantity: "",
      bags: [],
      rate: "",
      amount: "",
      discount: "",
      discount_type: "%",
      discount_amount: "",
      gst: null,
      gst_amount: "",
      total_amount: "",
      remark: "",
    },
    validationSchema: tableValidationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
      };

      // Validate bags
      if (!values.bags || values.bags.length === 0) {
        tableFormik.setFieldTouched("bags", true);
        toast.error("At least one bag is required");
        return;
      }

      // Calculate total issue quantity from all bags
      const totalIssueQuantity = values.bags.reduce((sum: number, bag: any) => {
        const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
        return sum + issueQty;
      }, 0);

      // Validate quantity field is not less than total issue quantity from bags
      const quantity = parseFloat(String(values.quantity)) || 0;
      if (quantity < totalIssueQuantity) {
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Quantity (${quantity}) cannot be less than Total Issue Quantity (${totalIssueQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      // Validate that quantity equals total issue quantity from bags
      if (Math.abs(quantity - totalIssueQuantity) > 0.01) {
        // Allow small floating point differences
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Quantity (${quantity}) must equal Total Issue Quantity (${totalIssueQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      // Calculate total available quantity from all bags
      const totalAvailableQuantity = values.bags.reduce(
        (sum: number, bag: any) => {
          const availableQty = parseFloat(String(bag.available_quantity)) || 0;
          return sum + availableQty;
        },
        0
      );

      // Validate that required quantity doesn't exceed total available stock
      if (quantity > totalAvailableQuantity) {
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Required Quantity (${quantity.toFixed(
            2
          )}) exceeds Total Available Stock (${totalAvailableQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      const currentItems = formik.values.items || [];

      if (editingItemId !== null) {
        // Update existing item
        const updatedItems = currentItems.map((item: any) =>
          item.id === editingItemId ? { ...payload, id: editingItemId } : item
        );
        formik.setFieldValue("items", updatedItems);
      } else {
        // Add new item
        const newItem = {
          ...payload,
          id: Date.now(), // Temporary ID for the item
        };
        formik.setFieldValue("items", [...currentItems, newItem]);
      }

      // Clear items validation error if it exists
      if (formik.touched.items && formik.errors.items) {
        formik.setFieldError("items", undefined);
      }

      // Reset table formik and editing state after adding/updating item
      tableFormik.resetForm();
      setEditingItemId(null);
    },
  });

  console.log("formik values", formik?.values);

  useEffect(() => {
    if (id) {
      setFormLoading(true);
      Axios.get(`${api?.quotation?.enquiry}/${id}/get`)
        .then((result: any) => {
          const data = result?.data?.data;
          setQuotationData(data);

          // formik.setFieldValue('quotation_date', data?.quotation_date?.split('T')?.[0]);
          formik.setFieldValue("remarks", data?.remarks || "");
          formik.setFieldValue("enquiry_no", data?.enquiry_number || "");
          formik.setFieldValue("enquiry_id", data?.enquiry_id || "");

          const updated_enquiry_items =
            data?.enquiry_items?.map((item: any, index: number) => {
              const rate = Number(item.selling_price);
              const qty = Number(item.required_quantity);
              const gst = Number(item.gst_percent);

              const amount = rate * qty;
              const gstAmount = (amount * gst) / 100;

              return {
                ...item,
                // Assign unique ID to each enquiry item to prevent all items from being updated when editing one
                id: Date.now() + index, // Unique ID for each item
                quotation_item_id: null,
                quotation_id: null,
                dispatch_from_warehouse_id: null,
                rate: item.selling_price,
                quantity: item.required_quantity,
                bags: [],
                amount: amount.toString(),
                discount: 0,
                discount_type: "%",
                discount_amount: 0,
                item: {
                  item_name: item?.item_name,
                  item_id: item?.item_id,
                },
                category: {
                  category_name: item?.category_name,
                  category_id: item?.category_id,
                },
                sub_category: {
                  sub_category_name: item?.sub_category_name,
                  sub_category_id: item?.sub_category_id,
                },
                gst: {
                  gst_percent: item?.gst_percent,
                  gst_id: item?.gst_id,
                },
                unit: {
                  unit_name: item?.unit_name,
                  unit_id: item?.unit_id,
                },
                // Preserve HSN code fields from enquiry item
                hsn_code_id: item?.hsn_code_id,
                hsn_code_name: item?.hsn_code_name,
                gst_amount: gstAmount.toString(),
                total_amount: (amount + gstAmount).toString(),
                item_details: item.item_details || {},
              };
            }) || [];
          formik.setFieldValue("items", updated_enquiry_items);

          // Initialize bagQuantityValidation with existing bags from all items
          const initialBagValidation: Array<{
            inventory_item_id: string | number;
            available_quantity: number;
            issue_quantity: number;
          }> = [];

          updated_enquiry_items.forEach((item: any) => {
            if (item.bags && Array.isArray(item.bags)) {
              item.bags.forEach((bag: any) => {
                const inventoryItemId = bag.inventory_item_id;
                const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
                const availableQty =
                  parseFloat(String(bag.available_quantity)) || 0;

                const existingBagIndex = initialBagValidation.findIndex(
                  (b) => String(b.inventory_item_id) === String(inventoryItemId)
                );

                if (existingBagIndex !== -1) {
                  // Bag exists, append issue quantity
                  initialBagValidation[existingBagIndex].issue_quantity +=
                    issueQty;
                } else {
                  // Bag doesn't exist, add new entry
                  initialBagValidation.push({
                    inventory_item_id: inventoryItemId,
                    available_quantity: availableQty,
                    issue_quantity: issueQty,
                  });
                }
              });
            }
          });

          setBagQuantityValidation(initialBagValidation);
        })
        .catch((err) => {
          toast.error("Failed to fetch quotation data");
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  }, [id]);
  console.log("updated_enquiry_items : ", formik?.values?.items);
  useEffect(() => {
    Axios.get(`${api?.master?.customer?.dropdown}`)
      .then((result) => {
        setCustomerOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch customer dropdown");
      });
    Axios.get(`${api?.master?.warehouse?.dropdown_all}`)
      .then((result) => {
        setDispatchWarehouseOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch warehouse dropdown");
      });
  }, []);

  // Set warehouse and customer values once dropdowns are loaded
  useEffect(() => {
    if (
      quotationData &&
      dispatchWarehouseOptions.length > 0 &&
      customerOptions.length > 0
    ) {
      // Map warehouse - find from dropdown options using dispatch_from_warehouse_id
      if (quotationData?.dispatch_from_warehouse_id) {
        const warehouse = dispatchWarehouseOptions.find(
          (w: any) =>
            w.warehouse_id === quotationData.dispatch_from_warehouse_id
        );
        if (warehouse) {
          formik.setFieldValue("dispatch_from_warehouse", warehouse);
        }
      }

      // Map customer - find from dropdown options using customer_id from customer_details
      if (quotationData?.customer_details?.customer_id) {
        const customer = customerOptions.find(
          (c: any) =>
            c.customer_id === quotationData.customer_details.customer_id
        );
        if (customer) {
          formik.setFieldValue("customer", customer);
        }
      }
    }
  }, [quotationData, dispatchWarehouseOptions, customerOptions]);

  // Handle QR scan result
  const handleQRScanResult = async (scannedValue: any) => {
    // Validate that dispatch from warehouse is selected
    if (
      !formik.values.dispatch_from_warehouse ||
      !formik.values.dispatch_from_warehouse?.warehouse_id
    ) {
      toast.error(
        "Please select Dispatch From warehouse before scanning items"
      );
      setShowScanner(false);
      return;
    }

    let inventoryItemId: string | null = null;

    // Extract inventory_item_id from scanned value
    if (typeof scannedValue === "string") {
      inventoryItemId = scannedValue;
    } else if (typeof scannedValue === "object" && scannedValue !== null) {
      inventoryItemId =
        scannedValue.inventory_item_id || scannedValue.id || scannedValue;
    }

    if (!inventoryItemId) {
      toast.error("Invalid QR code. Could not extract inventory item ID.");
      return;
    }

    try {
      setIsScanningItem(true);
      // Get warehouse_id to send as query parameter
      const warehouseId = formik.values.dispatch_from_warehouse?.warehouse_id;

      const response = await Axios.get(
        `${api.warehouse.dispatch.stock_transfer.inventory_item_by_id}${inventoryItemId}`,
        {
          params: {
            warehouse_id: warehouseId,
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        const itemData = response.data.data;

        // Check if item already exists in the list (by inventory_item_id in bags)
        const existingItem = formik.values.items?.find((item: any) => {
          if (item.bags && Array.isArray(item.bags)) {
            return item.bags.some(
              (bag: any) => bag.inventory_item_id === inventoryItemId
            );
          }
          return false;
        });

        if (existingItem) {
          toast.warning("Item already added to the list");
          setIsScanningItem(false);
          return;
        }

        // Validate available quantity is greater than 0
        const availableQty = parseFloat(String(itemData.available_quantity)) || 0;
        if (availableQty <= 0) {
          toast.error(
            "Bag cannot be added. Available quantity is zero or invalid."
          );
          setIsScanningItem(false);
          return;
        }

        // Create bag data
        const bagData = {
          bag_no: itemData.bag_no || "",
          inventory_item_id: itemData.inventory_item_id,
          available_quantity: itemData.available_quantity,
          issue_quantity: itemData.available_quantity, // Default to available quantity
        };

        // Validate required item fields
        if (!itemData.item?.item_id || !itemData.item?.item_code) {
          toast.error(
            "Item data is incomplete. Missing item ID or item code."
          );
          setIsScanningItem(false);
          return;
        }

        if (!itemData.item?.category_id || !itemData.item?.sub_category_id) {
          toast.error(
            "Item category or sub-category information is missing. Cannot add item."
          );
          setIsScanningItem(false);
          return;
        }

        // Get rate from master_item selling_price (from itemData.item.selling_price)
        const rate = parseFloat(String(itemData.item?.selling_price || 0));

        // Get GST percent from master_item hsn_code
        const gstPercent =
          itemData.item?.hsn_code?.gst_percent ||
          itemData.item?.gst_percent ||
          0;

        // Calculate amounts
        const quantity = itemData.available_quantity;
        const amount = parseFloat((quantity * rate).toFixed(2));
        const discount = 0;
        const discountType = "%";
        const discountAmount = 0;
        const netAmount = amount - discountAmount;
        const gstAmount = parseFloat(
          ((netAmount * parseFloat(String(gstPercent))) / 100).toFixed(2)
        );
        const totalAmount = parseFloat((netAmount + gstAmount).toFixed(2));

        // Create new quotation item from scanned inventory item
        const newItem = {
          id: Date.now(), // Temporary ID for the item
          category: {
            category_id: itemData.item.category_id,
            category_name: itemData.item.category_name || "",
          },
          sub_category: {
            sub_category_id: itemData.item.sub_category_id,
            sub_category_name: itemData.item.sub_category_name || "",
          },
          item: {
            item_id: itemData.item.item_id,
            item_name: itemData.item.item_name || "",
            item_code: itemData.item.item_code || "",
            unit: itemData.item.unit || {},
            selling_price: rate,
            hsn_code: itemData.item?.hsn_code || null,
          },
          item_code: itemData.item.item_code || "",
          unit: itemData.item?.unit_name || "",
          quantity: quantity,
          bags: [bagData],
          rate: rate,
          amount: amount,
          discount: discount,
          discount_type: discountType,
          discount_amount: discountAmount,
          gst: {
            gst_percent: parseFloat(String(gstPercent)),
          },
          gst_amount: gstAmount,
          total_amount: totalAmount,
          remark: itemData.remark || "",
        };

        // Add item directly to the items list
        const currentItems = formik.values.items || [];
        formik.setFieldValue("items", [...currentItems, newItem]);

        // Update bag quantity validation
        if (setBagQuantityValidation) {
          const existingBagIndex = bagQuantityValidation.findIndex(
            (b) => String(b.inventory_item_id) === String(inventoryItemId)
          );

          if (existingBagIndex !== -1) {
            // Bag exists, append issue quantity
            const updatedBags = [...bagQuantityValidation];
            updatedBags[existingBagIndex].issue_quantity += itemData.available_quantity;
            setBagQuantityValidation(updatedBags);
          } else {
            // Bag doesn't exist, add new entry
            setBagQuantityValidation([
              ...bagQuantityValidation,
              {
                inventory_item_id: inventoryItemId,
                available_quantity: itemData.available_quantity,
                issue_quantity: itemData.available_quantity,
              },
            ]);
          }
        }

        // Clear items validation error if it exists
        if (formik.touched.items && formik.errors.items) {
          formik.setFieldError("items", undefined);
        }

        // Clear any editing state when adding from scan
        setEditingItemId(null);

        toast.success("Item added successfully from QR scan");
        setShowScanner(false);
      } else {
        throw new Error("Failed to fetch inventory item");
      }
    } catch (error: any) {
      console.error("Error fetching inventory item:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch inventory item. Please try again.";

      // Check if error is about warehouse mismatch
      if (errorMessage.includes("not present in the selected warehouse")) {
        showError(errorMessage, "Warehouse Mismatch");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsScanningItem(false);
    }
  };

  console.log("id in line 496 in enquiry quotation form: ", id);

  return (
    <PageContainer>
      <FormHeader
        title="Create Quotation"
        firstLabel="Quotation"
        secondLabel="Create Quotation"
        handleArrowClick={() => navigate("/quotation")}
        handleBreadcrumbClick={() => navigate("/quotation")}
      />

      {formLoding ? (
        <LoadingSpinner label="Loading..." />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
          <Row>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Quotation Date<span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                name="quotation_date"
                value={formik.values.quotation_date}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("quotation_date", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.quotation_date &&
                  formik.touched.quotation_date
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.quotation_date}
              </Form.Control.Feedback>
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Dispatch From Warehouse<span className="text-danger">*</span>
              </Form.Label>
              <WarehouseAutocomplete
                size="small"
                options={dispatchWarehouseOptions || []}
                showWarehouseType={true}
                value={formik.values.dispatch_from_warehouse?.warehouse_id || null}
                onChange={async (warehouseId, warehouseName, fullOption) => {
                  // Check if warehouse is being changed
                  const currentWarehouse =
                    formik.values.dispatch_from_warehouse;
                  const currentWarehouseId = currentWarehouse?.warehouse_id;
                  const newWarehouseId = warehouseId || null;

                  // Handle clear case (when warehouse is cleared)
                  if (!warehouseId || !fullOption) {
                    // Check if any item has bags with bag_no (indicating warehouse has been selected for items)
                    const hasItemsWithBags =
                      formik.values.items &&
                      formik.values.items.some((item: any) => {
                        const bags = item?.bags;
                        return (
                          Array.isArray(bags) &&
                          bags.length > 0 &&
                          bags.some((bag: any) => bag?.bag_no)
                        );
                      });

                    // If warehouse is being cleared and items have bags, show confirmation
                    if (hasItemsWithBags && currentWarehouseId) {
                      const result = await showConfirm(
                        "Are you sure you want to change the warehouse as it will remove all the added item",
                        "Change Warehouse?",
                        "Yes",
                        "No"
                      );

                      if (result.isConfirmed) {
                        // User confirmed: clear items and clear warehouse
                        formik.setFieldValue("items", []);
                        formik.setFieldValue("dispatch_from_warehouse", null);
                        // Clear bag quantity validation
                        setBagQuantityValidation([]);
                        // Reset editing state
                        setEditingItemId(null);
                        toast.info(
                          "Warehouse changed. All items have been removed."
                        );
                      } else {
                        // User cancelled: revert warehouse to previous value
                        setTimeout(() => {
                          formik.setFieldValue(
                            "dispatch_from_warehouse",
                            currentWarehouse
                          );
                        }, 0);
                      }
                    } else {
                      // No items with bags - just clear warehouse
                      formik.setFieldValue("dispatch_from_warehouse", null);
                    }
                    return;
                  }

                  // Check if any item has bags with bag_no (indicating warehouse has been selected for items)
                  const hasItemsWithBags =
                    formik.values.items &&
                    formik.values.items.some((item: any) => {
                      const bags = item?.bags;
                      return (
                        Array.isArray(bags) &&
                        bags.length > 0 &&
                        bags.some((bag: any) => bag?.bag_no)
                      );
                    });

                  // If warehouse is changing and items have bags, show confirmation
                  if (
                    hasItemsWithBags &&
                    currentWarehouseId &&
                    currentWarehouseId !== newWarehouseId
                  ) {
                    const result = await showConfirm(
                      "Are you sure you want to change the warehouse as it will remove all the added item",
                      "Change Warehouse?",
                      "Yes",
                      "No"
                    );

                    if (result.isConfirmed) {
                      // User confirmed: clear items and update warehouse
                      formik.setFieldValue("items", []);
                      formik.setFieldValue("dispatch_from_warehouse", fullOption);
                      // Clear bag quantity validation
                      setBagQuantityValidation([]);
                      // Reset editing state
                      setEditingItemId(null);
                      toast.info(
                        "Warehouse changed. All items have been removed."
                      );
                    } else {
                      // User cancelled: revert warehouse to previous value
                      setTimeout(() => {
                        formik.setFieldValue(
                          "dispatch_from_warehouse",
                          currentWarehouse
                        );
                      }, 0);
                      return;
                    }
                  } else {
                    // No items with bags or warehouse not changing, update normally
                    formik.setFieldValue("dispatch_from_warehouse", fullOption);
                  }
                }}
                onBlur={() =>
                  formik.setFieldTouched("dispatch_from_warehouse", true)
                }
                placeholder="Select Warehouse"
                required
                formik={formik}
                name="dispatch_from_warehouse"
                valueType="warehouse_id"
                customSx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
                inputSx={{
                  background: "#FFF",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.dispatch_from_warehouse &&
                        formik.errors.dispatch_from_warehouse
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
              />
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label>
                Customer Name<span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                size="small"
                options={customerOptions || []}
                autoHighlight
                value={formik.values.customer}
                onChange={(_, val) => formik.setFieldValue("customer", val)}
                onBlur={() => formik.setFieldTouched("customer", true)}
                getOptionLabel={(option: any) =>
                  option ? option?.customer_name : ""
                }
                renderOption={(props, option: any) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt: any) => opt?.customer_name || ""
                  )
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Customer"
                    fullWidth
                    error={
                      formik.touched.customer && Boolean(formik.errors.customer)
                    }
                    helperText={
                      formik.touched.customer &&
                      typeof formik.errors.customer === "string"
                        ? formik.errors.customer
                        : undefined
                    }
                    sx={{
                      background: "#FFF",
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor:
                            formik.touched.customer && formik.errors.customer
                              ? "#dc3545"
                              : "#ced4da",
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        formik.touched.customer && formik.errors.customer
                          ? "#dc3545"
                          : "#ced4da",
                    },
                  },
                }}
              />
            </Col>
            <Col lg={3} className="mb-3">
              <Form.Label>Enquiry No.</Form.Label>
              <Form.Control
                type="text"
                name="enquiry_no"
                value={formik.values.enquiry_no || ""}
                disabled
                placeholder="Auto Fill"
              />
            </Col>
          </Row>
          <div className="mb-3">
            <div className="d-flex justify-content-end mb-2">
              <ActionButton
                label="Scan"
                icon="bi bi-qr-code-scan"
                handleClick={() => {
                  if (
                    !formik.values.dispatch_from_warehouse ||
                    !formik.values.dispatch_from_warehouse?.warehouse_id
                  ) {
                    toast.error(
                      "Please select Dispatch From warehouse before scanning items"
                    );
                    return;
                  }
                  setShowScanner(true);
                }}
                variant="primary"
                disabled={
                  !formik.values.dispatch_from_warehouse ||
                  !formik.values.dispatch_from_warehouse?.warehouse_id
                }
              />
            </div>
            {/* Dispatch From Warehouse is not selected */}
            {(!formik.values.dispatch_from_warehouse ||
              !formik.values.dispatch_from_warehouse?.warehouse_id) && (
              <div className="alert alert-warning mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Please select Dispatch From warehouse before scanning/adding
                items
              </div>
            )}
            {/* Loading indicator when scanning item */}
            {isScanningItem && (
              <div className="text-center py-3 mb-3">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Loading item from QR scan...</span>
              </div>
            )}
            <QuotationItemTable
              formik={tableFormik}
              mainFormik={formik}
              editingItemId={editingItemId}
              setEditingItemId={setEditingItemId}
              bagQuantityValidation={bagQuantityValidation}
              setBagQuantityValidation={setBagQuantityValidation}
              warehouseId={formik.values.dispatch_from_warehouse?.warehouse_id}
            />
          </div>
          {formik.touched.items && formik.errors.items && (
            <div
              className="text-danger mb-3"
              style={{ fontSize: "0.875rem", marginTop: "-15px" }}
            >
              {typeof formik.errors.items === "string"
                ? formik.errors.items
                : "At least one item is required"}
            </div>
          )}

          {/* Remarks and Financial Summary Section */}
          <Row className="mb-4">
            <Col lg={7} className="mb-3">
              <div
                style={{
                  border: `1px solid ${Colors?.background_color}`,
                  borderRadius: "4px",
                }}
              >
                <div
                  className="d-flex align-items-center gap-2 mb-0 py-2 px-1"
                  style={{ background: Colors?.background_color }}
                >
                  <i
                    className="bi bi-chevron-down"
                    style={{ fontSize: "0.675rem", color: "#000" }}
                  ></i>
                  <h6 className="mb-0">Remarks</h6>
                </div>
                <Form.Control
                  as="textarea"
                  rows={6}
                  name="remarks"
                  placeholder="Enter your remarks"
                  value={formik.values.remarks}
                  onChange={(e) =>
                    formik.setFieldValue("remarks", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  style={{
                    border: "1px solid #transparant",
                    borderRadius: "0px",
                    resize: "vertical",
                  }}
                />
              </div>
            </Col>
            <Col lg={5} className="mb-3">
              <div
                style={{
                  backgroundColor: Colors?.background_color,
                  padding: "20px",
                  borderRadius: "8px",
                  border: `1px solid ${Colors?.background_color}`,
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Subtotal (Before Discount):
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Total Item Discounts:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.discount_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Net Taxable Value:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {(
                      formik.values.items?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.amount || 0);
                      }, 0) -
                      formik.values.items?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.discount_amount || 0);
                      }, 0)
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                    Total GST:
                  </span>
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.gst_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
                <hr style={{ margin: "12px 0", borderColor: "#90caf9" }} />
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: "#212529",
                    }}
                  >
                    Total Amount:
                  </span>
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "700",
                      color: "#212529",
                    }}
                  >
                    ₹{" "}
                    {formik.values.items
                      ?.reduce((sum: number, item: any) => {
                        return sum + parseFloat(item.total_amount || 0);
                      }, 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to="/quotation" />
            <SubmitButton isSubmitting={formLoding} isEdit={!!id} />
          </div>
        </Form>
      )}
      <QRScanner
        show={showScanner}
        handleClose={() => setShowScanner(false)}
        onResult={handleQRScanResult}
      />
    </PageContainer>
  );
};

export default EnquiryQuotationForm;
